import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useSearchParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import PageLayout from "../../components/PageLayout";
import GrowthAccountingChart from "./GrowthAccountingChart";
import { ComputedStatus, Segmented } from "./ReportParts";
import {
  CohortPanel,
  POPULATIONS,
  StickinessPanel,
  useRetentionData,
} from "./RetentionPanels";
import { cachedFetch } from "../../http/analyticsCache";
import { ExperimentsPanel, useExperimentData } from "./ExperimentsPanel";
import {
  getDeviceOpenStats,
  getTutorialStats,
  getEngagementStats,
  getThemeEngagementStats,
  getGrowthAccounting,
} from "../../http/analytics";
import ThemeEngagementPanel from "./ThemeEngagementPanel";

dayjs.extend(utc);
dayjs.extend(timezone);
const { RangePicker } = DatePicker;

const EST_TZ = "America/New_York";
const DEFAULT_RANGE_DAYS = 30;

// Reports sub-tabs: one per question, all sharing the filters above them.
const TABS = [
  { id: "acquisition", label: "Acquisition" },
  { id: "engagement", label: "Engagement" },
  { id: "theme-engagement", label: "Theme Engagement" },
  { id: "retention", label: "Retention" },
  { id: "onboarding", label: "Onboarding" },
  { id: "experiments", label: "Experiments" },
];

/** Section heading; any explanation lives in a hover info icon. */
const SectionHeading = ({ children, info, first }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 0.75,
      mt: first ? 0 : "32px",
      mb: "12px",
    }}
  >
    <Typography
      sx={{
        fontWeight: 700,
        fontSize: { xs: "16px", md: "18px" },
        color: "#142C5C",
      }}
    >
      {children}
    </Typography>
    {info && (
      <Tooltip title={info} placement="right">
        <InfoOutlinedIcon
          sx={{ fontSize: 17, color: "text.disabled", cursor: "help" }}
        />
      </Tooltip>
    )}
  </Box>
);

const headerCellStyle = {
  fontWeight: 700,
  color: "#fff",
  backgroundColor: "#142C5C",
  fontSize: { xs: "12px", sm: "13px", md: "14px" },
  whiteSpace: "nowrap",
};

/**
 * Per-tutorial display semantics. `skippable: false` shows completed-only
 * values (the profile tutorial only has an Okay path). `skippedHint` covers
 * the VS feed migration-day backfill; home/launchpad were never backfilled.
 * Unknown types from a newer backend fall back to showing everything.
 */
const TUTORIAL_META = {
  vsFeed: {
    skippable: true,
    skippedHint: "All time, includes backfilled existing users",
  },
  profile: { skippable: false },
  home: { skippable: true, skippedHint: "All time" },
  launchpad: { skippable: true, skippedHint: "All time" },
};

/** Placeholder sections shown while the backend has no tutorial data yet. */
const TUTORIAL_PLACEHOLDERS = [
  { type: "vsFeed", label: "VS Feed Tutorial" },
  { type: "profile", label: "Profile Tutorial" },
  { type: "home", label: "Home Tutorial" },
  { type: "launchpad", label: "Launchpad Tutorial" },
].map((t) => ({ ...t, daily: [], totals: { completed: 0, skipped: 0 } }));

/**
 * What the in-progress week still needs to finish at `target`.
 *
 * Quick ratio = inflow / churned, inflow = new + resurrected. Two levers,
 * held apart so each answer is a single number:
 * - more inflow (a signup who is active, or someone returning after a gap)
 *   raises the numerator and leaves churn where it is;
 * - a user active last week who comes back counts as retained, so inflow
 *   stays put and churn shrinks.
 * Already at or above the target → both gaps are 0. Churned = 0 → the ratio
 * is infinite, so nothing more is required.
 */
const quickRatioGap = (week, target) => {
  const inflow = week.newUsers + week.resurrected;
  const { churned } = week;
  const current = churned === 0 ? null : inflow / churned;
  if (!(target > 0) || !Number.isFinite(target)) {
    return { inflow, churned, current, valid: false };
  }
  if (churned === 0 || current >= target) {
    return {
      inflow,
      churned,
      current,
      valid: true,
      reached: true,
      moreInflow: 0,
      returnsNeeded: 0,
    };
  }
  // Smallest integer inflow that satisfies inflow' / churned >= target.
  const moreInflow = Math.max(0, Math.ceil(target * churned - inflow - 1e-9));
  // Largest churn still allowed: floor(inflow / target). Returning everyone
  // (churned → 0) makes the ratio infinite, which clears any finite target
  // even when inflow is 0.
  const churnLeft = Math.floor(inflow / target);
  const returnsNeeded = Math.max(0, churned - churnLeft);
  return {
    inflow,
    churned,
    current,
    valid: true,
    reached: false,
    moreInflow,
    returnsNeeded,
    inflowRatio: churned === 0 ? null : (inflow + moreInflow) / churned,
    returnRatio: churnLeft === 0 ? null : inflow / churnLeft,
  };
};

const formatRatio = (value) => (value == null ? "∞" : value.toFixed(2));

const QuickRatioPlanner = ({ week }) => {
  const [targetText, setTargetText] = useState("1");
  const target = Number(targetText);
  const gap = quickRatioGap(week, target);

  return (
    <Paper
      elevation={0}
      sx={{
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        padding: "16px",
        marginBottom: "16px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 2,
          marginBottom: "8px",
        }}
      >
        <Typography
          sx={{ fontWeight: 700, fontSize: "14px", color: "#142C5C" }}
        >
          Reach a quick ratio this week
        </Typography>
        <TextField
          size="small"
          type="number"
          label="Target"
          value={targetText}
          onChange={(event) => setTargetText(event.target.value)}
          inputProps={{ min: 0, step: 0.1 }}
          sx={{ width: 120 }}
        />
        <Typography sx={{ fontSize: "13px", color: "text.secondary" }}>
          {dayjs(week.weekStart).format("MMM D")} –{" "}
          {dayjs(week.weekEnd).format("MMM D")}
          {week.isPartial ? " · in progress" : ""} · now{" "}
          {formatRatio(gap.current)} ({gap.inflow.toLocaleString()} in)
        </Typography>
      </Box>
      {!gap.valid ? (
        <Typography sx={{ fontSize: "13px", color: "text.secondary" }}>
          Enter a target above 0.
        </Typography>
      ) : gap.reached ? (
        <Typography sx={{ fontSize: "13px", color: "#2e7d32" }}>
          Already at or above {formatRatio(target)}
          {gap.churned === 0
            ? " — nothing has churned, so the ratio is infinite."
            : "."}
        </Typography>
      ) : (
        <Typography
          sx={{ fontSize: "13px", color: "#142C5C", lineHeight: 1.6 }}
        >
          Either {gap.moreInflow.toLocaleString()} more active users who are new
          or coming back after a gap (ratio becomes{" "}
          {formatRatio(gap.inflowRatio)}), or{" "}
          {gap.returnsNeeded.toLocaleString()} users who were active last week
          come back before the week ends (they count as retained, ratio becomes{" "}
          {formatRatio(gap.returnRatio)}).
        </Typography>
      )}
    </Paper>
  );
};

/** Current date string (YYYY-MM-DD) in the selected display timezone. */
const todayInTimeZone = (timeZone) =>
  timeZone === "UTC"
    ? dayjs().utc().format("YYYY-MM-DD")
    : dayjs().tz(EST_TZ).format("YYYY-MM-DD");

/**
 * The backend only returns days that had at least one open or registration;
 * fill the rest of the selected range with zeros so every day is visible,
 * newest first.
 */
const buildDailyRows = (daily, startDate, endDate) => {
  if (!startDate || !endDate) return daily;
  const byDate = new Map(daily.map((d) => [d.date, d]));
  const rows = [];
  const start = dayjs(startDate);
  let cursor = dayjs(endDate);
  while (cursor.isAfter(start, "day") || cursor.isSame(start, "day")) {
    const key = cursor.format("YYYY-MM-DD");
    const day = byDate.get(key);
    rows.push({
      date: key,
      uniqueDevices: day?.uniqueDevices || 0,
      registrations: day?.registrations || 0,
      byModel: day?.byModel || [],
    });
    cursor = cursor.subtract(1, "day");
  }
  return rows;
};

/**
 * Same zero-fill, but one row per day with completed/skipped values for
 * every tutorial keyed by type, so all tutorials share a single table.
 */
const buildCombinedTutorialRows = (tutorials, startDate, endDate) => {
  if (!startDate || !endDate) return [];
  const byType = new Map(
    tutorials.map((t) => [
      t.type,
      new Map((t.daily || []).map((d) => [d.date, d])),
    ])
  );
  const rows = [];
  const start = dayjs(startDate);
  let cursor = dayjs(endDate);
  while (cursor.isAfter(start, "day") || cursor.isSame(start, "day")) {
    const key = cursor.format("YYYY-MM-DD");
    rows.push({
      date: key,
      byType: Object.fromEntries(
        tutorials.map((t) => {
          const day = byType.get(t.type)?.get(key);
          return [
            t.type,
            { completed: day?.completed || 0, skipped: day?.skipped || 0 },
          ];
        })
      ),
    });
    cursor = cursor.subtract(1, "day");
  }
  return rows;
};

/** Daily engagement table columns (order = display order). */
const ENGAGEMENT_COLUMNS = [
  { key: "activeUsers", label: "Active Users" },
  { key: "signups", label: "Signups" },
  { key: "logins", label: "Logins" },
  { key: "votes", label: "Votes" },
  { key: "posts", label: "Posts" },
  { key: "comments", label: "Comments" },
  { key: "likes", label: "Likes" },
  { key: "follows", label: "Follows" },
  { key: "matchesStarted", label: "Matches" },
  { key: "liveMatches", label: "Live Matches" },
];

/** Same zero-fill as the other daily tables, for engagement rows. */
const buildEngagementDailyRows = (daily, startDate, endDate) => {
  if (!startDate || !endDate) return daily;
  const byDate = new Map(daily.map((d) => [d.date, d]));
  const rows = [];
  const start = dayjs(startDate);
  let cursor = dayjs(endDate);
  while (cursor.isAfter(start, "day") || cursor.isSame(start, "day")) {
    const key = cursor.format("YYYY-MM-DD");
    const day = byDate.get(key) || {};
    rows.push({
      date: key,
      ...Object.fromEntries(
        ENGAGEMENT_COLUMNS.map((c) => [c.key, day[c.key] || 0])
      ),
    });
    cursor = cursor.subtract(1, "day");
  }
  return rows;
};

const SummaryCard = ({ label, value, hint }) => (
  <Paper
    elevation={0}
    sx={{
      border: "1px solid #e0e0e0",
      borderRadius: "8px",
      padding: "16px 20px",
      minWidth: { xs: "100%", sm: "220px" },
      flex: { xs: "1 1 100%", sm: "0 1 auto" },
    }}
  >
    <Typography
      sx={{
        fontSize: { xs: "12px", md: "13px" },
        color: "text.secondary",
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: { xs: "24px", md: "30px" },
        fontWeight: 700,
        color: "#142C5C",
        lineHeight: 1.3,
      }}
    >
      {value}
    </Typography>
    {hint && (
      <Typography sx={{ fontSize: "11px", color: "text.secondary" }}>
        {hint}
      </Typography>
    )}
  </Paper>
);

export default function Conversion() {
  const [timeZone, setTimeZone] = useState("EST");
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(DEFAULT_RANGE_DAYS - 1, "day"),
    dayjs(),
  ]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("all");
  const [stats, setStats] = useState({
    daily: [],
    totalUniqueDevices: 0,
    // null = the backend didn't report registrations (older deploy); the
    // registration card/column degrade instead of showing a false 0.
    totalRegistrations: null,
    // null = the backend didn't report by-model breakdown (older deploy).
    totalByModel: null,
    startDate: null,
    endDate: null,
  });
  const [tutorialStats, setTutorialStats] = useState({
    tutorials: [],
    startDate: null,
    endDate: null,
    loading: true,
    unavailable: false,
  });
  const [engagementStats, setEngagementStats] = useState({
    daily: [],
    totals: null,
    startDate: null,
    endDate: null,
    loading: true,
    unavailable: false,
  });
  const [themeEngagementStats, setThemeEngagementStats] = useState({
    themes: [],
    totals: null,
    startDate: null,
    endDate: null,
    loading: true,
    unavailable: false,
  });
  const [growthStats, setGrowthStats] = useState({
    weeks: [],
    loading: true,
    unavailable: false,
  });

  const fetchStats = useCallback(
    async (force = false) => {
      setLoading(true);
      try {
        const params = { timezone: timeZone };
        if (dateRange?.[0] && dateRange?.[1]) {
          params.startDate = dayjs(dateRange[0]).format("YYYY-MM-DD");
          params.endDate = dayjs(dateRange[1]).format("YYYY-MM-DD");
        }
        const response = await cachedFetch(
          "deviceOpens",
          getDeviceOpenStats,
          params,
          { force }
        );
        const data = response?.data || {};
        setStats({
          daily: data.daily || [],
          totalUniqueDevices: data.totalUniqueDevices || 0,
          totalRegistrations: data.totalRegistrations ?? null,
          totalByModel: Array.isArray(data.totalByModel)
            ? data.totalByModel
            : null,
          startDate: data.startDate || null,
          endDate: data.endDate || null,
          cachedAt: data.cachedAt || null,
        });
      } catch (error) {
        console.error("Failed to load conversion stats:", error);
        toast.error("Failed to load conversion stats");
      } finally {
        setLoading(false);
      }
    },
    [timeZone, dateRange]
  );

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const fetchTutorialStats = useCallback(
    async (force = false) => {
      setTutorialStats((prev) => ({ ...prev, loading: true }));
      try {
        const params = { timezone: timeZone };
        if (dateRange?.[0] && dateRange?.[1]) {
          params.startDate = dayjs(dateRange[0]).format("YYYY-MM-DD");
          params.endDate = dayjs(dateRange[1]).format("YYYY-MM-DD");
        }
        const response = await cachedFetch(
          "tutorialStats",
          getTutorialStats,
          params,
          { force }
        );
        const data = response?.data || {};
        setTutorialStats({
          tutorials: data.tutorials || [],
          startDate: data.startDate || null,
          endDate: data.endDate || null,
          cachedAt: data.cachedAt || null,
          loading: false,
          unavailable: false,
        });
      } catch (error) {
        // Deliberately no toast: this endpoint ships with a newer backend, so
        // until that deploys the section degrades to an inline notice instead
        // of error-toasting admins who came for device stats.
        console.error("Failed to load tutorial stats:", error);
        setTutorialStats((prev) => ({
          ...prev,
          tutorials: [],
          loading: false,
          unavailable: true,
        }));
      }
    },
    [timeZone, dateRange]
  );

  useEffect(() => {
    fetchTutorialStats();
  }, [fetchTutorialStats]);

  // "Users" selector: applies to every panel on Engagement and Retention
  const [population, setPopulation] = useState("activated");

  const fetchEngagementStats = useCallback(
    async (force = false) => {
      setEngagementStats((prev) => ({ ...prev, loading: true }));
      try {
        const params = { timezone: timeZone, population };
        if (dateRange?.[0] && dateRange?.[1]) {
          params.startDate = dayjs(dateRange[0]).format("YYYY-MM-DD");
          params.endDate = dayjs(dateRange[1]).format("YYYY-MM-DD");
        }
        const response = await cachedFetch(
          "engagementStats",
          getEngagementStats,
          params,
          { force }
        );
        const data = response?.data || {};
        setEngagementStats({
          daily: data.daily || [],
          totals: data.totals || null,
          startDate: data.startDate || null,
          endDate: data.endDate || null,
          cachedAt: data.cachedAt || null,
          loading: false,
          unavailable: false,
        });
      } catch (error) {
        // No toast: this endpoint ships with a newer backend; until it deploys
        // the section degrades to an inline notice (same as tutorial stats).
        console.error("Failed to load engagement stats:", error);
        setEngagementStats((prev) => ({
          ...prev,
          daily: [],
          totals: null,
          loading: false,
          unavailable: true,
        }));
      }
    },
    [timeZone, dateRange, population]
  );

  useEffect(() => {
    fetchEngagementStats();
  }, [fetchEngagementStats]);

  const fetchThemeEngagementStats = useCallback(
    async (force = false) => {
      setThemeEngagementStats((prev) => ({ ...prev, loading: true }));
      try {
        const params = { timezone: timeZone };
        if (dateRange?.[0] && dateRange?.[1]) {
          params.startDate = dayjs(dateRange[0]).format("YYYY-MM-DD");
          params.endDate = dayjs(dateRange[1]).format("YYYY-MM-DD");
        }
        const response = await cachedFetch(
          "themeEngagement",
          getThemeEngagementStats,
          params,
          { force }
        );
        const data = response?.data || {};
        setThemeEngagementStats({
          themes: data.themes || [],
          totals: data.totals || null,
          startDate: data.startDate || null,
          endDate: data.endDate || null,
          cachedAt: data.cachedAt || null,
          loading: false,
          unavailable: false,
        });
      } catch (error) {
        console.error("Failed to load theme engagement stats:", error);
        setThemeEngagementStats((prev) => ({
          ...prev,
          themes: [],
          totals: null,
          loading: false,
          unavailable: true,
        }));
      }
    },
    [timeZone, dateRange]
  );

  useEffect(() => {
    fetchThemeEngagementStats();
  }, [fetchThemeEngagementStats]);

  const fetchGrowthAccounting = useCallback(
    async (force = false) => {
      setGrowthStats((prev) => ({ ...prev, loading: true }));
      try {
        const params = { timezone: timeZone, population };
        if (dateRange?.[0] && dateRange?.[1]) {
          params.startDate = dayjs(dateRange[0]).format("YYYY-MM-DD");
          params.endDate = dayjs(dateRange[1]).format("YYYY-MM-DD");
        }
        const response = await cachedFetch(
          "growthAccounting",
          getGrowthAccounting,
          params,
          { force }
        );
        const data = response?.data || {};
        setGrowthStats({
          weeks: data.weeks || [],
          cachedAt: data.cachedAt || null,
          loading: false,
          unavailable: false,
        });
      } catch (error) {
        // Same graceful degradation as the other newer-backend sections.
        console.error("Failed to load growth accounting:", error);
        setGrowthStats((prev) => ({
          ...prev,
          weeks: [],
          loading: false,
          unavailable: true,
        }));
      }
    },
    [timeZone, dateRange, population]
  );

  useEffect(() => {
    fetchGrowthAccounting();
  }, [fetchGrowthAccounting]);

  const [searchParams, setSearchParams] = useSearchParams();
  const tab = TABS.some((t) => t.id === searchParams.get("tab"))
    ? searchParams.get("tab")
    : "acquisition";
  const setTab = (id) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", id);
        return next;
      },
      { replace: true }
    );
  const { experiment, loadExperiment } = useExperimentData();
  const {
    stick,
    ret,
    load: loadRetention,
  } = useRetentionData({
    timeZone,
    dateRange,
    population,
  });

  // Status and refresh follow whatever the current tab shows.
  const tabSources = {
    acquisition: [{ loading, cachedAt: stats.cachedAt }],
    engagement: [
      stick,
      { ...engagementStats, cachedAt: engagementStats.cachedAt },
    ].map((x) => ({
      loading: x.loading,
      cachedAt: x.cachedAt ?? x.data?.cachedAt,
    })),
    "theme-engagement": [
      {
        loading: themeEngagementStats.loading,
        cachedAt: themeEngagementStats.cachedAt,
      },
    ],
    retention: [ret, growthStats].map((x) => ({
      loading: x.loading,
      cachedAt: x.cachedAt ?? x.data?.cachedAt,
    })),
    onboarding: [
      { loading: tutorialStats.loading, cachedAt: tutorialStats.cachedAt },
    ],
    experiments: [
      { loading: experiment.loading, cachedAt: experiment.data?.cachedAt },
    ],
  }[tab];
  const tabLoading = tabSources.some((x) => x.loading);
  const tabComputedAt = tabSources
    .map((x) => x.cachedAt)
    .filter(Boolean)
    .sort()[0];
  const refreshTab = () => {
    if (tab === "acquisition") fetchStats(true);
    if (tab === "engagement") {
      loadRetention(true);
      fetchEngagementStats(true);
    }
    if (tab === "theme-engagement") fetchThemeEngagementStats(true);
    if (tab === "retention") {
      loadRetention(true);
      fetchGrowthAccounting(true);
    }
    if (tab === "onboarding") fetchTutorialStats(true);
    if (tab === "experiments") loadExperiment(true);
  };

  const dailyRows = useMemo(
    () => buildDailyRows(stats.daily, stats.startDate, stats.endDate),
    [stats]
  );

  const today = todayInTimeZone(timeZone);
  const todayRow = dailyRows.find((row) => row.date === today);
  const registrationsSupported = stats.totalRegistrations !== null;
  const byModelSupported = Array.isArray(stats.totalByModel);
  const showByModel = viewMode === "byModel" && byModelSupported;

  const byModelDailyRows = useMemo(() => {
    return dailyRows.flatMap((row) => {
      const models = (row.byModel || []).filter((m) => m?.model);
      if (!models.length) {
        return [
          {
            date: row.date,
            model: row.uniqueDevices > 0 ? "Unknown" : "—",
            uniqueDevices: row.uniqueDevices,
          },
        ];
      }
      return models.map((m) => ({
        date: row.date,
        model: m.model,
        uniqueDevices: m.uniqueDevices || 0,
      }));
    });
  }, [dailyRows]);

  const todayByModel = (todayRow?.byModel || []).filter(
    (m) => m?.uniqueDevices > 0
  );

  const engagementRows = useMemo(
    () =>
      buildEngagementDailyRows(
        engagementStats.daily,
        engagementStats.startDate,
        engagementStats.endDate
      ),
    [engagementStats]
  );
  const todayEngagementRow = engagementRows.find((row) => row.date === today);

  const tutorials = tutorialStats.tutorials.length
    ? tutorialStats.tutorials
    : TUTORIAL_PLACEHOLDERS;
  const tutorialRows = useMemo(
    () =>
      buildCombinedTutorialRows(
        tutorials,
        tutorialStats.startDate,
        tutorialStats.endDate
      ),
    [tutorials, tutorialStats.startDate, tutorialStats.endDate]
  );

  return (
    <PageLayout title="Analytics">
      <ToastContainer />

      {tab !== "experiments" && (
        <>
          {/* Filters */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: { xs: 1.5, md: 3 },
              marginBottom: "20px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flex: { xs: "1 1 100%", sm: "0 1 auto" },
                minWidth: 0,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "12px", sm: "14px", md: "15px" },
                  paddingRight: "10px",
                  whiteSpace: "nowrap",
                }}
              >
                Date Range:
              </Typography>
              <RangePicker
                value={dateRange}
                onChange={(dates) => setDateRange(dates || [null, null])}
                format="YYYY-MM-DD"
                allowClear={false}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flex: { xs: "1 1 100%", sm: "0 1 auto" },
                minWidth: 0,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "12px", sm: "14px", md: "15px" },
                  paddingRight: "10px",
                  whiteSpace: "nowrap",
                }}
              >
                Timezone:
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  borderRadius: "6px",
                  overflow: "hidden",
                  border: "1px solid #d9d9d9",
                }}
              >
                {["EST", "UTC"].map((tz) => (
                  <Box
                    key={tz}
                    onClick={() => setTimeZone(tz)}
                    sx={{
                      padding: "4px 14px",
                      fontSize: { xs: "12px", sm: "13px", md: "14px" },
                      fontWeight: 600,
                      cursor: "pointer",
                      backgroundColor: timeZone === tz ? "#142C5C" : "#fff",
                      color: timeZone === tz ? "#fff" : "#333",
                      transition: "all 0.2s",
                      "&:hover": {
                        backgroundColor:
                          timeZone === tz ? "#142C5C" : "#f0f0f0",
                      },
                    }}
                  >
                    {tz}
                  </Box>
                ))}
              </Box>
            </Box>
            {(tab === "engagement" || tab === "retention") && (
              <Segmented
                label="Users"
                options={POPULATIONS}
                value={population}
                onChange={setPopulation}
              />
            )}
            {byModelSupported && tab === "acquisition" && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flex: { xs: "1 1 100%", sm: "0 1 auto" },
                  minWidth: 0,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: "12px", sm: "14px", md: "15px" },
                    paddingRight: "10px",
                    whiteSpace: "nowrap",
                  }}
                >
                  View:
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    borderRadius: "6px",
                    overflow: "hidden",
                    border: "1px solid #d9d9d9",
                  }}
                >
                  {[
                    { id: "all", label: "All" },
                    { id: "byModel", label: "By Model" },
                  ].map((option) => (
                    <Box
                      key={option.id}
                      onClick={() => setViewMode(option.id)}
                      sx={{
                        padding: "4px 14px",
                        fontSize: { xs: "12px", sm: "13px", md: "14px" },
                        fontWeight: 600,
                        cursor: "pointer",
                        backgroundColor:
                          viewMode === option.id ? "#142C5C" : "#fff",
                        color: viewMode === option.id ? "#fff" : "#333",
                        transition: "all 0.2s",
                        "&:hover": {
                          backgroundColor:
                            viewMode === option.id ? "#142C5C" : "#f0f0f0",
                        },
                      }}
                    >
                      {option.label}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </>
      )}

      {/* Tabs */}
      <Box
        role="tablist"
        sx={{
          display: "flex",
          gap: 0.5,
          flexWrap: "wrap",
          borderBottom: "1px solid #e0e0e0",
          mb: "24px",
        }}
      >
        {TABS.map((t) => (
          <Box
            key={t.id}
            role="tab"
            aria-selected={tab === t.id}
            tabIndex={0}
            onClick={() => setTab(t.id)}
            onKeyDown={(e) =>
              (e.key === "Enter" || e.key === " ") && setTab(t.id)
            }
            sx={{
              padding: "10px 16px",
              fontSize: { xs: "13px", md: "15px" },
              fontWeight: 700,
              cursor: "pointer",
              color: tab === t.id ? "#142C5C" : "text.secondary",
              borderBottom:
                tab === t.id ? "3px solid #142C5C" : "3px solid transparent",
              mb: "-1px",
              "&:hover": { color: "#142C5C" },
            }}
          >
            {t.label}
          </Box>
        ))}
        <ComputedStatus
          loading={tabLoading}
          computedAt={tabComputedAt}
          onRefresh={refreshTab}
        />
      </Box>

      {tab === "acquisition" && (
        <>
          {/* Summary cards */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              marginBottom: "24px",
            }}
          >
            <SummaryCard
              label="Total Unique Apps"
              value={stats.totalUniqueDevices.toLocaleString()}
              hint="All time, every unique app that has ever opened the app"
            />
            <SummaryCard
              label="Total Registrations"
              value={
                registrationsSupported
                  ? stats.totalRegistrations.toLocaleString()
                  : "—"
              }
              hint={
                registrationsSupported
                  ? "All time, every account ever registered (incl. since-deleted)"
                  : "Requires the newer backend"
              }
            />
            <SummaryCard
              label={`Today (${timeZone})`}
              value={
                todayRow
                  ? registrationsSupported
                    ? `${todayRow.uniqueDevices.toLocaleString()} / ${todayRow.registrations.toLocaleString()}`
                    : todayRow.uniqueDevices.toLocaleString()
                  : "—"
              }
              hint={
                todayRow
                  ? showByModel && todayByModel.length
                    ? todayByModel
                        .map(
                          (m) =>
                            `${m.model}: ${m.uniqueDevices.toLocaleString()}`
                        )
                        .join(" · ")
                    : registrationsSupported
                    ? "Apps opened / accounts registered today"
                    : "Unique apps that opened the app today"
                  : "Today is outside the selected date range"
              }
            />
          </Box>

          {showByModel && (
            <>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "16px", md: "18px" },
                  color: "#142C5C",
                  marginBottom: "4px",
                }}
              >
                Unique Apps by Model
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "12px", md: "13px" },
                  color: "text.secondary",
                  marginBottom: "16px",
                }}
              >
                All-time unique apps grouped by device model. Apps that have not
                reported a model yet appear as iOS/Android (unknown model) until
                they next open the app.
              </Typography>
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                  border: "1px solid #e0e0e0",
                  borderRadius: "8px",
                  maxHeight: "40vh",
                  marginBottom: "24px",
                }}
              >
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...headerCellStyle, width: "50%" }}>
                        Model
                      </TableCell>
                      <TableCell sx={headerCellStyle}>Unique Apps</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stats.totalByModel.length ? (
                      stats.totalByModel.map((row) => (
                        <TableRow key={row.model} hover>
                          <TableCell
                            sx={{
                              fontSize: { xs: "12px", md: "14px" },
                              fontWeight: 600,
                              color: "#142C5C",
                            }}
                          >
                            {row.model}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontSize: { xs: "12px", md: "14px" },
                              fontWeight: row.uniqueDevices > 0 ? 600 : 400,
                              color:
                                row.uniqueDevices > 0
                                  ? "#142C5C"
                                  : "text.secondary",
                            }}
                          >
                            {row.uniqueDevices.toLocaleString()}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={2}
                          align="center"
                          sx={{ padding: "32px" }}
                        >
                          {loading ? "Loading…" : "No unique apps recorded yet"}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {/* Daily table */}
          {showByModel && (
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: { xs: "16px", md: "18px" },
                color: "#142C5C",
                marginBottom: "12px",
              }}
            >
              Daily Unique Apps by Model
            </Typography>
          )}
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: "8px",
              maxHeight: "60vh",
            }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      ...headerCellStyle,
                      width: showByModel ? "40%" : "50%",
                    }}
                  >
                    Date ({timeZone})
                  </TableCell>
                  {showByModel && (
                    <TableCell sx={headerCellStyle}>Model</TableCell>
                  )}
                  <TableCell sx={headerCellStyle}>Unique Apps</TableCell>
                  {registrationsSupported && !showByModel && (
                    <TableCell sx={headerCellStyle}>Registrations</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {(showByModel ? byModelDailyRows : dailyRows).length ? (
                  (showByModel ? byModelDailyRows : dailyRows).map(
                    (row, index) => (
                      <TableRow
                        key={
                          showByModel
                            ? `${row.date}-${row.model}-${index}`
                            : row.date
                        }
                        hover
                      >
                        <TableCell
                          sx={{
                            fontSize: { xs: "12px", md: "14px" },
                            whiteSpace: "nowrap",
                          }}
                        >
                          {dayjs(row.date).format("MMM D, YYYY")}
                          <Typography
                            component="span"
                            sx={{
                              color: "text.secondary",
                              fontSize: { xs: "11px", md: "12px" },
                              marginLeft: "8px",
                            }}
                          >
                            {dayjs(row.date).format("ddd")}
                            {row.date === today ? " · Today" : ""}
                          </Typography>
                        </TableCell>
                        {showByModel && (
                          <TableCell
                            sx={{
                              fontSize: { xs: "12px", md: "14px" },
                              fontWeight:
                                row.model && row.model !== "—" ? 600 : 400,
                              color:
                                row.model && row.model !== "—"
                                  ? "#142C5C"
                                  : "text.secondary",
                            }}
                          >
                            {row.model}
                          </TableCell>
                        )}
                        <TableCell
                          sx={{
                            fontSize: { xs: "12px", md: "14px" },
                            fontWeight: row.uniqueDevices > 0 ? 600 : 400,
                            color:
                              row.uniqueDevices > 0
                                ? "#142C5C"
                                : "text.secondary",
                          }}
                        >
                          {row.uniqueDevices.toLocaleString()}
                        </TableCell>
                        {registrationsSupported && !showByModel && (
                          <TableCell
                            sx={{
                              fontSize: { xs: "12px", md: "14px" },
                              fontWeight: row.registrations > 0 ? 600 : 400,
                              color:
                                row.registrations > 0
                                  ? "#142C5C"
                                  : "text.secondary",
                            }}
                          >
                            {row.registrations.toLocaleString()}
                          </TableCell>
                        )}
                      </TableRow>
                    )
                  )
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={showByModel ? 3 : registrationsSupported ? 3 : 2}
                      align="center"
                      sx={{ padding: "32px" }}
                    >
                      {loading ? "Loading…" : "No unique apps recorded yet"}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Engagement — every signal per day, same filters */}
        </>
      )}

      {tab === "engagement" && (
        <>
          <SectionHeading first>Stickiness</SectionHeading>
          <StickinessPanel stick={stick} timeZone={timeZone} />
          <SectionHeading info="Every engagement signal per day. Active Users counts distinct accounts that logged in, voted, posted, commented, liked, followed someone, started a match, or signed up. Follows of the official @pagent account are left out of Follows and Active Users — every account is followed onto it automatically, including a one-time backfill of existing accounts, so those rows are not engagement. Live Matches is the subset of Matches that were real-time LIVE battles. Follows the Users selector: with Activated, only users with 5+ votes count, from the day they activated, and Signups shows accounts that went on to activate.">
            Daily activity
          </SectionHeading>

          {engagementStats.unavailable ? (
            <Paper
              elevation={0}
              sx={{
                border: "1px dashed #c9c9c9",
                borderRadius: "8px",
                padding: "20px",
                color: "text.secondary",
                fontSize: "13px",
                marginBottom: "8px",
              }}
            >
              Engagement stats are not available yet — the backend serving this
              panel does not expose /analytics/admin/engagement-stats.
            </Paper>
          ) : (
            <>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 2,
                  marginBottom: "16px",
                }}
              >
                <SummaryCard
                  label={`Active Users Today (${timeZone})`}
                  value={
                    todayEngagementRow
                      ? todayEngagementRow.activeUsers.toLocaleString()
                      : "—"
                  }
                  hint={
                    todayEngagementRow
                      ? "Distinct accounts that did anything today"
                      : "Today is outside the selected date range"
                  }
                />
                <SummaryCard
                  label="Active Users (Range)"
                  value={
                    engagementStats.totals
                      ? engagementStats.totals.activeUsers.toLocaleString()
                      : "—"
                  }
                  hint="Distinct accounts active in the selected range"
                />
                <SummaryCard
                  label="Signups (Range)"
                  value={
                    engagementStats.totals
                      ? engagementStats.totals.signups.toLocaleString()
                      : "—"
                  }
                  hint="Accounts registered in the selected range"
                />
              </Box>
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                  border: "1px solid #e0e0e0",
                  borderRadius: "8px",
                  maxHeight: "60vh",
                  marginBottom: "8px",
                }}
              >
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headerCellStyle}>
                        Date ({timeZone})
                      </TableCell>
                      {ENGAGEMENT_COLUMNS.map((col) => (
                        <TableCell key={col.key} sx={headerCellStyle}>
                          {col.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {engagementRows.length ? (
                      engagementRows.map((row) => (
                        <TableRow key={row.date} hover>
                          <TableCell
                            sx={{
                              fontSize: { xs: "12px", md: "14px" },
                              whiteSpace: "nowrap",
                            }}
                          >
                            {dayjs(row.date).format("MMM D, YYYY")}
                            <Typography
                              component="span"
                              sx={{
                                color: "text.secondary",
                                fontSize: { xs: "11px", md: "12px" },
                                marginLeft: "8px",
                              }}
                            >
                              {dayjs(row.date).format("ddd")}
                              {row.date === today ? " · Today" : ""}
                            </Typography>
                          </TableCell>
                          {ENGAGEMENT_COLUMNS.map((col) => (
                            <TableCell
                              key={col.key}
                              sx={{
                                fontSize: { xs: "12px", md: "14px" },
                                fontWeight: row[col.key] > 0 ? 600 : 400,
                                color:
                                  row[col.key] > 0
                                    ? "#142C5C"
                                    : "text.secondary",
                              }}
                            >
                              {row[col.key].toLocaleString()}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={ENGAGEMENT_COLUMNS.length + 1}
                          align="center"
                          sx={{ padding: "32px" }}
                        >
                          {engagementStats.loading
                            ? "Loading…"
                            : "No engagement recorded yet"}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {/* Growth accounting — weekly leaky-bucket flows, same filters */}
        </>
      )}

      {tab === "theme-engagement" && (
        <ThemeEngagementPanel stats={themeEngagementStats} />
      )}

      {tab === "retention" && (
        <>
          <SectionHeading first>Cohorts</SectionHeading>
          <CohortPanel ret={ret} timeZone={timeZone} />
          <SectionHeading info="Leaky-bucket view per week (Mon–Sun): active users (same definition as Engagement, so automatic follows of @pagent do not count) split into new (signed up that week), retained (also active the week before) and resurrected (returned after a gap), against churned (active the week before, absent that week). Quick Ratio = (new + resurrected) ÷ churned — below 1 the bucket leaks: the app loses more users than it gains. Follows the Users selector: with Activated, users join the bucket the week they reach 5 votes, and New means activated that week.">
            Growth accounting
          </SectionHeading>

          {growthStats.unavailable ? (
            <Paper
              elevation={0}
              sx={{
                border: "1px dashed #c9c9c9",
                borderRadius: "8px",
                padding: "20px",
                color: "text.secondary",
                fontSize: "13px",
              }}
            >
              Growth accounting is not available yet — the backend serving this
              panel does not expose /analytics/admin/growth-accounting.
            </Paper>
          ) : (
            <>
              {growthStats.weeks.length > 0 && (
                <GrowthAccountingChart weeks={growthStats.weeks} />
              )}
              {growthStats.weeks.some((week) => week.isPartial) ? (
                <QuickRatioPlanner
                  week={growthStats.weeks.find((week) => week.isPartial)}
                />
              ) : (
                !growthStats.loading && (
                  <Typography
                    sx={{
                      fontSize: "13px",
                      color: "text.secondary",
                      marginBottom: "16px",
                    }}
                  >
                    The current week is outside the selected dates, so there is
                    nothing left to move. Include today to see what this week
                    still needs.
                  </Typography>
                )
              )}
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                  border: "1px solid #e0e0e0",
                  borderRadius: "8px",
                  maxHeight: "60vh",
                }}
              >
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...headerCellStyle, width: "24%" }}>
                        Week ({timeZone})
                      </TableCell>
                      <TableCell sx={headerCellStyle}>Active</TableCell>
                      <TableCell sx={headerCellStyle}>New</TableCell>
                      <TableCell sx={headerCellStyle}>Retained</TableCell>
                      <TableCell sx={headerCellStyle}>Resurrected</TableCell>
                      <TableCell sx={headerCellStyle}>Churned</TableCell>
                      <TableCell sx={headerCellStyle}>Quick Ratio</TableCell>
                      <TableCell sx={headerCellStyle}>Net</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {growthStats.weeks.length ? (
                      growthStats.weeks.map((week) => {
                        const leaky =
                          week.quickRatio !== null && week.quickRatio < 1;
                        return (
                          <TableRow key={week.weekStart} hover>
                            <TableCell
                              sx={{
                                fontSize: { xs: "12px", md: "14px" },
                                whiteSpace: "nowrap",
                              }}
                            >
                              {dayjs(week.weekStart).format("MMM D")} –{" "}
                              {dayjs(week.weekEnd).format("MMM D, YYYY")}
                              {week.isPartial && (
                                <Typography
                                  component="span"
                                  sx={{
                                    color: "text.secondary",
                                    fontSize: { xs: "11px", md: "12px" },
                                    marginLeft: "8px",
                                  }}
                                >
                                  · in progress
                                </Typography>
                              )}
                            </TableCell>
                            {[
                              week.active,
                              week.newUsers,
                              week.retained,
                              week.resurrected,
                            ].map((value, i) => (
                              <TableCell
                                // eslint-disable-next-line react/no-array-index-key
                                key={i}
                                sx={{
                                  fontSize: { xs: "12px", md: "14px" },
                                  fontWeight: value > 0 ? 600 : 400,
                                  color:
                                    value > 0 ? "#142C5C" : "text.secondary",
                                }}
                              >
                                {value.toLocaleString()}
                              </TableCell>
                            ))}
                            <TableCell
                              sx={{
                                fontSize: { xs: "12px", md: "14px" },
                                fontWeight:
                                  !week.isPartial && week.churned > 0
                                    ? 600
                                    : 400,
                                color:
                                  week.isPartial || week.churned === 0
                                    ? "text.secondary"
                                    : "#142C5C",
                              }}
                            >
                              {week.isPartial
                                ? "—"
                                : week.churned.toLocaleString()}
                            </TableCell>
                            <TableCell
                              sx={{
                                fontSize: { xs: "12px", md: "14px" },
                                fontWeight: 700,
                                color: week.isPartial
                                  ? "text.secondary"
                                  : leaky
                                  ? "#c62828"
                                  : "#2e7d32",
                              }}
                            >
                              {week.quickRatio === null
                                ? "∞"
                                : week.quickRatio.toFixed(2)}
                            </TableCell>
                            <TableCell
                              sx={{
                                fontSize: { xs: "12px", md: "14px" },
                                fontWeight: 600,
                                color: week.isPartial
                                  ? "text.secondary"
                                  : week.net < 0
                                  ? "#c62828"
                                  : "#2e7d32",
                              }}
                            >
                              {week.net > 0
                                ? `+${week.net.toLocaleString()}`
                                : week.net.toLocaleString()}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={8}
                          align="center"
                          sx={{ padding: "32px" }}
                        >
                          {growthStats.loading
                            ? "Loading…"
                            : "No activity recorded yet"}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {/* Tutorials — completed vs skipped per tutorial type, same filters */}
        </>
      )}

      {tab === "onboarding" && (
        <>
          <SectionHeading
            first
            info="Accounts that completed or skipped each tutorial; values show completed / skipped (the profile tutorial has no skip, so it shows completed only). Only the VS feed tutorial was backfilled (existing users marked skipped on migration day); profile, home and launchpad tutorials count every account on its first visit."
          >
            Tutorials
          </SectionHeading>

          {tutorialStats.unavailable ? (
            <Paper
              elevation={0}
              sx={{
                border: "1px dashed #c9c9c9",
                borderRadius: "8px",
                padding: "20px",
                color: "text.secondary",
                fontSize: "13px",
              }}
            >
              Tutorial stats are not available yet — the backend serving this
              panel does not expose /analytics/admin/tutorial-stats.
            </Paper>
          ) : (
            <>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 2,
                  marginBottom: "16px",
                }}
              >
                {tutorials.map((tutorial) => {
                  const meta = TUTORIAL_META[tutorial.type] || {
                    skippable: true,
                  };
                  return (
                    <SummaryCard
                      key={tutorial.type}
                      label={tutorial.label || tutorial.type}
                      value={
                        meta.skippable
                          ? `${(
                              tutorial.totals?.completed || 0
                            ).toLocaleString()} / ${(
                              tutorial.totals?.skipped || 0
                            ).toLocaleString()}`
                          : (tutorial.totals?.completed || 0).toLocaleString()
                      }
                      hint={
                        meta.skippable
                          ? `Completed / skipped · ${
                              meta.skippedHint || "All time"
                            }`
                          : "Completed · All time (not skippable)"
                      }
                    />
                  );
                })}
              </Box>
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                  border: "1px solid #e0e0e0",
                  borderRadius: "8px",
                  maxHeight: "60vh",
                }}
              >
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headerCellStyle}>
                        Date ({timeZone})
                      </TableCell>
                      {tutorials.map((tutorial) => (
                        <TableCell key={tutorial.type} sx={headerCellStyle}>
                          {tutorial.label || tutorial.type}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tutorialRows.length ? (
                      tutorialRows.map((row) => (
                        <TableRow key={row.date} hover>
                          <TableCell
                            sx={{
                              fontSize: { xs: "12px", md: "14px" },
                              whiteSpace: "nowrap",
                            }}
                          >
                            {dayjs(row.date).format("MMM D, YYYY")}
                            <Typography
                              component="span"
                              sx={{
                                color: "text.secondary",
                                fontSize: { xs: "11px", md: "12px" },
                                marginLeft: "8px",
                              }}
                            >
                              {dayjs(row.date).format("ddd")}
                              {row.date === today ? " · Today" : ""}
                            </Typography>
                          </TableCell>
                          {tutorials.map((tutorial) => {
                            const meta = TUTORIAL_META[tutorial.type] || {
                              skippable: true,
                            };
                            const cell = row.byType[tutorial.type] || {
                              completed: 0,
                              skipped: 0,
                            };
                            const hasActivity =
                              cell.completed > 0 ||
                              (meta.skippable && cell.skipped > 0);
                            return (
                              <TableCell
                                key={tutorial.type}
                                sx={{
                                  fontSize: { xs: "12px", md: "14px" },
                                  whiteSpace: "nowrap",
                                  fontWeight: hasActivity ? 600 : 400,
                                  color: hasActivity
                                    ? "#142C5C"
                                    : "text.secondary",
                                }}
                              >
                                {meta.skippable
                                  ? `${cell.completed.toLocaleString()} / ${cell.skipped.toLocaleString()}`
                                  : cell.completed.toLocaleString()}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={tutorials.length + 1}
                          align="center"
                          sx={{ padding: "32px" }}
                        >
                          {tutorialStats.loading
                            ? "Loading…"
                            : "No tutorial activity recorded yet"}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </>
      )}
      {tab === "experiments" && (
        <ExperimentsPanel
          experiment={experiment}
          onChanged={() => loadExperiment(true)}
        />
      )}
    </PageLayout>
  );
}
